import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, Check, X, Loader2, Clock, Inbox, History, AlertTriangle } from "lucide-react";
import { sr } from "../../lib/sr";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function AdminClientApprovals() {
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState("");
  const [rejecting, setRejecting] = useState(null); // user id being rejected
  const [reason, setReason] = useState("");
  const [tab, setTab] = useState("pending");
  const [history, setHistory] = useState(null);

  const load = () => sr.clientApprovals()
    .then((d) => { setRows(d.requests || []); setDenied(false); })
    .catch((e) => { if (e?.response?.status === 403) setDenied(true); setRows([]); });
  const loadHistory = () => sr.clientApprovalHistory()
    .then((d) => setHistory(d.history || []))
    .catch(() => setHistory([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "history" && history === null) loadHistory(); }, [tab]); // eslint-disable-line

  const approve = async (r) => {
    setBusy(r.id);
    try {
      const res = await sr.approveClient(r.id);
      toast.success(`Approved ${r.tenant_name}${res.emailed ? " — approval email sent" : ""}`);
      setRows((p) => p.filter((x) => x.id !== r.id));
      setHistory(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't approve"); }
    finally { setBusy(""); }
  };
  const reject = async (r) => {
    if (!reason.trim()) { toast.error("Add a short reason so the client understands"); return; }
    setBusy(r.id);
    try {
      await sr.rejectClient(r.id, reason.trim());
      toast.success(`Declined ${r.tenant_name}`);
      setRows((p) => p.filter((x) => x.id !== r.id));
      setRejecting(null); setReason(""); setHistory(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't decline"); }
    finally { setBusy(""); }
  };

  return (
    <div style={{ maxWidth: 860 }} data-testid="client-approvals-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 26, display: "inline-flex", alignItems: "center", gap: 10 }}><UserCheck size={24} /> Client approvals</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Approve new business account requests so they can sign in and start their first campaign.</p>
        </div>
        <button className="sr-btn sr-btn-sm" onClick={() => (tab === "pending" ? load() : loadHistory())} data-testid="approvals-refresh">Refresh</button>
      </div>

      {!denied && (
        <div style={{ display: "inline-flex", gap: 6, marginTop: 16, background: "var(--surface)", padding: 4, borderRadius: 10 }} data-testid="approvals-tabs">
          <button className={`sr-btn sr-btn-sm ${tab === "pending" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setTab("pending")} data-testid="approvals-tab-pending">
            <Inbox size={13} /> Pending{Array.isArray(rows) && rows.length ? ` (${rows.length})` : ""}
          </button>
          <button className={`sr-btn sr-btn-sm ${tab === "history" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setTab("history")} data-testid="approvals-tab-history">
            <History size={13} /> History
          </button>
        </div>
      )}

      {denied ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 44 }} data-testid="approvals-denied">
          <div style={{ width: 56, height: 56, borderRadius: 999, background: "var(--surface)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><UserCheck size={28} color="var(--muted)" /></div>
          <h3 style={{ marginTop: 12 }}>Staff only</h3>
          <p className="sr-muted" style={{ fontSize: 14, marginTop: 4 }}>Only SmartReach admins can review and approve client accounts.</p>
        </div>
      ) : tab === "history" ? (
        history === null ? (
          <div className="sr-card" style={{ textAlign: "center", padding: 40, marginTop: 16 }}><Loader2 className="sr-spin" /></div>
        ) : history.length === 0 ? (
          <div className="sr-card" style={{ textAlign: "center", padding: 44, marginTop: 16 }} data-testid="history-empty">
            <div style={{ width: 56, height: 56, borderRadius: 999, background: "var(--surface)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><History size={28} color="var(--muted)" /></div>
            <h3 style={{ marginTop: 12 }}>No decisions yet</h3>
            <p className="sr-muted" style={{ fontSize: 14, marginTop: 4 }}>Approvals and declines will be logged here with who decided and why.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10, marginTop: 16 }} data-testid="approvals-history">
            {history.map((h) => (
              <div key={h.id} className="sr-card" data-testid={`history-row-${h.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span className={`sr-badge ${h.action === "approved" ? "sr-badge-green" : ""}`} style={h.action === "declined" ? { background: "#FEF3F2", color: "#B42318" } : {}}>
                        {h.action === "approved" ? <Check size={11} /> : <X size={11} />} {h.action === "approved" ? "Approved" : "Declined"}
                      </span>
                      <strong style={{ fontSize: 15.5, color: "var(--navy)" }}>{h.business_name}</strong>
                    </div>
                    <p className="sr-muted" style={{ fontSize: 13.5, marginTop: 6 }}>{h.email}</p>
                    {h.action === "declined" && h.reason && (
                      <p style={{ fontSize: 13, marginTop: 4, color: "#B42318" }} data-testid={`history-reason-${h.id}`}>Reason: {h.reason}</p>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="sr-muted" style={{ fontSize: 12.5 }}>by {h.actor_name || h.actor_email || "staff"}</div>
                    <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{timeAgo(h.created_at)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : rows === null ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 40, marginTop: 16 }}><Loader2 className="sr-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 44, marginTop: 16 }} data-testid="approvals-empty">
          <div style={{ width: 56, height: 56, borderRadius: 999, background: "var(--surface)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Inbox size={28} color="var(--muted)" /></div>
          <h3 style={{ marginTop: 12 }}>No pending requests</h3>
          <p className="sr-muted" style={{ fontSize: 14, marginTop: 4 }}>New signups will appear here for you to approve.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          {rows.map((r) => (
            <div key={r.id} className="sr-card" data-testid={`approval-row-${r.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="sr-badge sr-badge-amber"><Clock size={11} /> Pending</span>
                    {r.overdue && (
                      <span className="sr-badge" style={{ background: "#FEF3F2", color: "#B42318" }} data-testid={`overdue-${r.id}`}>
                        <AlertTriangle size={11} /> Overdue · waiting {r.waiting_hours >= 48 ? `${Math.round(r.waiting_hours / 24)}d` : `${Math.round(r.waiting_hours)}h`}
                      </span>
                    )}
                    <strong style={{ fontSize: 16, color: "var(--navy)" }}>{r.tenant_name}</strong>
                  </div>
                  <p className="sr-muted" style={{ fontSize: 13.5, marginTop: 6 }}>{r.detail} · requested {timeAgo(r.created_at)}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy === r.id} onClick={() => approve(r)} data-testid={`approve-${r.id}`}>
                    {busy === r.id ? <Loader2 size={13} className="sr-spin" /> : <><Check size={13} /> Approve</>}
                  </button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy === r.id} onClick={() => { setRejecting(rejecting === r.id ? null : r.id); setReason(""); }} data-testid={`reject-toggle-${r.id}`}>
                    <X size={13} /> Decline
                  </button>
                </div>
              </div>
              {rejecting === r.id && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }} data-testid={`reject-form-${r.id}`}>
                  <input className="sr-input" style={{ flex: 1 }} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (shown to the client), e.g. Could not verify business" data-testid={`reject-reason-${r.id}`} />
                  <button className="sr-btn sr-btn-sm" style={{ background: "#B42318", color: "#fff", borderColor: "#B42318" }} disabled={busy === r.id} onClick={() => reject(r)} data-testid={`reject-confirm-${r.id}`}>Confirm decline</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
